/**
 * General utility functions for HTML5 Canvas operations
 * These functions can be used across different modules for canvas analysis and manipulation
 */

/**
 * Calculates the number of pixels in a canvas that differ from the background color
 * This is useful for determining if a canvas has content drawn on it
 *
 * @param canvas - The HTML canvas element to analyze
 * @param bgColor - The background color to compare against (defaults to white)
 * @returns The count of pixels that differ from the background color
 */
export const calculatePixelCount = (
    canvas: HTMLCanvasElement,
    bgColor: string = '#FFFFFF',
): number => {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
        console.log('calculatePixelCount: No context available');
        return 0;
    }

    try {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        let pixelCount = 0;

        // Parse background color to RGB values
        const bgRgb = bgColor.startsWith('#')
            ? {
                  r: parseInt(bgColor.slice(1, 3), 16) || 255,
                  g: parseInt(bgColor.slice(3, 5), 16) || 255,
                  b: parseInt(bgColor.slice(5, 7), 16) || 255,
              }
            : { r: 255, g: 255, b: 255 }; // Default to white if parsing fails

        // Count pixels that are different from background color
        // ImageData.data is a Uint8ClampedArray with RGBA values
        // Each pixel has 4 values: [R, G, B, A]
        for (let i = 0; i < data.length; i += 4) {
            const r = data[i] ?? 0;
            const g = data[i + 1] ?? 0;
            const b = data[i + 2] ?? 0;

            // Check if pixel color is different from background color
            if (r !== bgRgb.r || g !== bgRgb.g || b !== bgRgb.b) {
                pixelCount++;
            }
        }

        return pixelCount;
    } catch (error) {
        console.log('calculatePixelCount error:', error);
        return 0;
    }
};

/**
 * Analyzes a canvas and returns detailed information about its content
 *
 * @param canvas - The HTML canvas element to analyze
 * @param bgColor - The background color to compare against (defaults to white)
 * @returns Object containing pixel count, color map, and other analysis data
 */
export const analyzeCanvas = (
    canvas: HTMLCanvasElement,
    bgColor: string = '#FFFFFF',
): {
    pixelCount: number;
    colorMap: { [colorCode: string]: number };
    uniqueColors: number;
    totalPixels: number;
    hasContent: boolean;
} => {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
        console.log('analyzeCanvas: No context available');
        return {
            pixelCount: 0,
            colorMap: {},
            uniqueColors: 0,
            totalPixels: 0,
            hasContent: false,
        };
    }

    try {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        let pixelCount = 0;
        const colorMap: { [key: string]: number } = {};

        // Parse background color to RGB values
        const bgRgb = bgColor.startsWith('#')
            ? {
                  r: parseInt(bgColor.slice(1, 3), 16) || 255,
                  g: parseInt(bgColor.slice(3, 5), 16) || 255,
                  b: parseInt(bgColor.slice(5, 7), 16) || 255,
              }
            : { r: 255, g: 255, b: 255 }; // Default to white if parsing fails

        // Count pixels that are different from background color and build color map
        for (let i = 0; i < data.length; i += 4) {
            const r = data[i] ?? 0;
            const g = data[i + 1] ?? 0;
            const b = data[i + 2] ?? 0;

            // Check if pixel color is different from background color
            if (r !== bgRgb.r || g !== bgRgb.g || b !== bgRgb.b) {
                pixelCount++;

                // Create color code from RGB values in hex format
                const colorCode = `#${r.toString(16).padStart(2, '0')}${g
                    .toString(16)
                    .padStart(
                        2,
                        '0',
                    )}${b.toString(16).padStart(2, '0')}`.toUpperCase();
                colorMap[colorCode] = (colorMap[colorCode] || 0) + 1;
            }
        }

        const totalPixels = canvas.width * canvas.height;
        const uniqueColors = Object.keys(colorMap).length;
        const hasContent = pixelCount > 0;

        return {
            pixelCount,
            colorMap,
            uniqueColors,
            totalPixels,
            hasContent,
        };
    } catch (error) {
        console.log('analyzeCanvas error:', error);
        return {
            pixelCount: 0,
            colorMap: {},
            uniqueColors: 0,
            totalPixels: 0,
            hasContent: false,
        };
    }
};

/**
 * Converts RGB values to hex color code
 *
 * @param r - Red value (0-255)
 * @param g - Green value (0-255)
 * @param b - Blue value (0-255)
 * @returns Hex color code in format #RRGGBB
 */
export const rgbToHex = (r: number, g: number, b: number): string => {
    return `#${r.toString(16).padStart(2, '0')}${g
        .toString(16)
        .padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase();
};

/**
 * Parses a hex color string to RGB values
 *
 * @param hex - Hex color string (e.g., "#FF0000" or "FF0000")
 * @returns Object with r, g, b values or null if parsing fails
 */
export const hexToRgb = (
    hex: string,
): { r: number; g: number; b: number } | null => {
    const cleanHex = hex.replace('#', '');

    if (cleanHex.length !== 6) {
        return null;
    }

    const r = parseInt(cleanHex.slice(0, 2), 16);
    const g = parseInt(cleanHex.slice(2, 4), 16);
    const b = parseInt(cleanHex.slice(4, 6), 16);

    if (isNaN(r) || isNaN(g) || isNaN(b)) {
        return null;
    }

    return { r, g, b };
};
